import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaArrowLeft, FaShieldAlt, FaExclamationTriangle, FaCalculator, FaClipboardList, FaCheckCircle, FaFileAlt, FaRedoAlt, FaOilCan } from 'react-icons/fa';
import { productsAPI } from '../services/api';
import StatusBadge from '../components/StatusBadge';

// "Product 360" — everything related to one product (trains, risk/impact,
// worst-case + governing MACO, sampling plans, results, CV documents,
// revalidation) in one place, each section linking out to its own module.
// Most modules don't support deep-linking to one exact record yet (see the
// Dashboard "My Work" card for the same limitation) — these open the list page.

const Section = ({ icon: Icon, title, action, children }) => (
  <div className="card" style={{ marginBottom: '16px' }}>
    <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Icon /><strong>{title}</strong></div>
      {action}
    </div>
    <div style={{ padding: '14px 16px' }}>{children}</div>
  </div>
);

const Empty = ({ children }) => <p className="text-muted" style={{ margin: 0 }}>{children}</p>;

const ProductOverview = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    productsAPI.getOverview(productId)
      .then((res) => { if (alive) setData(res.data.data); })
      .catch((err) => {
        toast.error(err?.response?.data?.message || 'Failed to load product overview');
        if (alive) navigate('/product-master');
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [productId, navigate]);

  if (loading) return <div className="page-container"><p className="text-muted">Loading...</p></div>;
  if (!data) return null;

  const { product, trains, risk_assessments, impact_assessment, worst_case, maco, sampling_plans, results, result_counts, cv_documents, revalidation } = data;

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <button className="btn btn-outline btn-sm" onClick={() => navigate('/product-master')} style={{ marginBottom: '8px' }}>
            <FaArrowLeft /> Back to Product Master
          </button>
          <h1>{product.product_name}</h1>
          <p className="text-muted">{product.product_code} · {product.dosage_form} · <StatusBadge status={product.status} /></p>
        </div>
      </div>

      <Section icon={FaOilCan} title="Equipment Trains" action={<button className="btn btn-outline btn-sm" onClick={() => navigate('/equipment-trains')}>Open Equipment Trains</button>}>
        {trains.length ? (
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {trains.map((t) => <span key={t.train_id} className="tag" style={{ background: 'var(--surface-light)' }}>{t.train_code || t.train_name}</span>)}
          </div>
        ) : <Empty>No equipment train linked yet.</Empty>}
      </Section>

      <Section icon={FaShieldAlt} title="Risk Assessment" action={<button className="btn btn-outline btn-sm" onClick={() => navigate('/risk-assessment')}>Open Risk Assessment</button>}>
        {risk_assessments.length ? (
          <table><thead><tr><th>Status</th><th>Risk Level</th><th>Score</th><th>Updated</th></tr></thead>
            <tbody>{risk_assessments.map((r) => (
              <tr key={r.id}><td><StatusBadge status={r.status} /></td><td>{r.risk_level || '—'}</td><td>{r.total_risk_score ?? '—'}</td>
                <td>{r.updated_at ? new Date(r.updated_at).toLocaleDateString() : (r.created_at ? new Date(r.created_at).toLocaleDateString() : '—')}</td></tr>
            ))}</tbody>
          </table>
        ) : <Empty>No risk assessment on record for this product.</Empty>}
      </Section>

      <Section icon={FaExclamationTriangle} title="Impact Assessment" action={<button className="btn btn-outline btn-sm" onClick={() => navigate('/risk-assessment')}>Open</button>}>
        {impact_assessment ? (
          <p style={{ margin: 0 }}>
            <StatusBadge status={impact_assessment.status} />{' '}
            {impact_assessment.cv_required ? 'CV required' : 'CV not required'}
            {impact_assessment.updated_at && <span className="text-muted"> · updated {new Date(impact_assessment.updated_at).toLocaleDateString()}</span>}
          </p>
        ) : <Empty>No impact assessment on record.</Empty>}
      </Section>

      <Section icon={FaOilCan} title="Worst-Case Status" action={<button className="btn btn-outline btn-sm" onClick={() => navigate('/worst-case')}>Open Worst Case</button>}>
        {worst_case.length ? (
          <ul style={{ margin: 0, paddingLeft: '18px' }}>
            {worst_case.map((w) => (
              <li key={w.id}>
                {w.product_id === product.product_id ? 'This product is the current worst case' : 'Another product is the current worst case'} for train #{w.train_id}
                {w.signed_at && <span className="text-muted"> · signed {new Date(w.signed_at).toLocaleDateString()}</span>}
              </li>
            ))}
          </ul>
        ) : <Empty>No worst-case decision on record for this product's train(s).</Empty>}
      </Section>

      <Section icon={FaCalculator} title="Governing MACO" action={<button className="btn btn-outline btn-sm" onClick={() => navigate('/maco')}>Open MACO</button>}>
        {maco.length ? (
          <table><thead><tr><th>Train</th><th>Governing MACO</th><th>Role</th><th>QA Approved</th><th>Calculated</th></tr></thead>
            <tbody>{maco.map((m) => (
              <tr key={m.maco_id}>
                <td>#{m.train_id}</td>
                <td>{m.calculated_maco}</td>
                <td>{m.next_product_id === product.product_id ? 'Incoming (worst case)' : m.previous_product_id === product.product_id ? 'Previous' : '—'}</td>
                <td>{m.qa_approved ? <FaCheckCircle style={{ color: 'var(--success-text)' }} /> : 'Pending'}</td>
                <td>{m.calculated_at ? new Date(m.calculated_at).toLocaleDateString() : '—'}</td>
              </tr>
            ))}</tbody>
          </table>
        ) : <Empty>No governing MACO calculated yet for this product's train(s).</Empty>}
      </Section>

      <Section icon={FaClipboardList} title="Sampling Plans" action={<button className="btn btn-outline btn-sm" onClick={() => navigate('/acceptance-criteria')}>Open Sampling Plans</button>}>
        {sampling_plans.length ? (
          <table><thead><tr><th>Plan</th><th>Study Type</th><th>Status</th><th>Updated</th></tr></thead>
            <tbody>{sampling_plans.map((p) => (
              <tr key={p.plan_id}><td>{p.plan_code}</td><td>{p.study_type}</td><td><StatusBadge status={p.status} /></td>
                <td>{p.updated_at ? new Date(p.updated_at).toLocaleDateString() : '—'}</td></tr>
            ))}</tbody>
          </table>
        ) : <Empty>No sampling plans for this product's train(s) yet.</Empty>}
      </Section>

      <Section icon={FaCheckCircle} title="Results" action={<button className="btn btn-outline btn-sm" onClick={() => navigate('/results')}>Open Results</button>}>
        {results.length ? (
          <>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '10px' }}>
              {Object.entries(result_counts).map(([status, count]) => (
                <span key={status} className="tag" style={{ background: 'var(--surface-light)' }}>{status}: {count}</span>
              ))}
            </div>
            <table><thead><tr><th>Batch</th><th>Result</th><th>Status</th><th>Sample Date</th></tr></thead>
              <tbody>{results.slice(0, 10).map((r) => (
                <tr key={r.result_id}><td>{r.batch_number || '—'}</td><td>{r.result_status || '—'}</td><td>{r.status}</td>
                  <td>{r.sample_date ? new Date(r.sample_date).toLocaleDateString() : '—'}</td></tr>
              ))}</tbody>
            </table>
          </>
        ) : <Empty>No results recorded yet for this product's sampling plans.</Empty>}
      </Section>

      <Section icon={FaFileAlt} title="CV Documents" action={<button className="btn btn-outline btn-sm" onClick={() => navigate('/study-report')}>Open Study Report</button>}>
        {cv_documents.length ? (
          <table><thead><tr><th>Document</th><th>Type</th><th>Status</th><th>Updated</th></tr></thead>
            <tbody>{cv_documents.map((d) => (
              <tr key={d.doc_id}><td>{d.document_number} v{d.version}</td><td>{d.doc_type}</td><td><StatusBadge status={d.status} /></td>
                <td>{d.updated_at ? new Date(d.updated_at).toLocaleDateString() : '—'}</td></tr>
            ))}</tbody>
          </table>
        ) : <Empty>No protocol/report generated yet for this product's sampling plans.</Empty>}
      </Section>

      <Section icon={FaRedoAlt} title="Revalidation" action={<button className="btn btn-outline btn-sm" onClick={() => navigate('/revalidation')}>Open Revalidation</button>}>
        {revalidation.length ? (
          <table><thead><tr><th>Trigger</th><th>Type</th><th>Status</th><th>Triggered</th></tr></thead>
            <tbody>{revalidation.map((r) => (
              <tr key={r.trigger_id}><td>{r.trigger_type}</td><td>{r.revalidation_type || '—'}</td><td>{r.status}</td>
                <td>{r.triggered_at ? new Date(r.triggered_at).toLocaleDateString() : '—'}</td></tr>
            ))}</tbody>
          </table>
        ) : <Empty>No revalidation triggers on record for this product.</Empty>}
      </Section>
    </div>
  );
};

export default ProductOverview;
